import { tryFormatDate, findById } from '../utils';
import i18n from '../i18n';
import { OverviewValueType, ValidationData } from '../api/dashboard';
import { MultiPicturesModel } from '../data_entry/edit/data_entry_edit_model';
import { MAX_DECIMALS } from '../models/value_meta';

function formatNumber(value: {}, validation: ValidationData, fixedDecimals: boolean = false): string {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return value.toString();
  }
  const maxDecimals = validation.max_decimals ?? MAX_DECIMALS;
  // maximumFractionDigits must be within [0, 20]; clamp defensively against out-of-range config.
  const clampedDecimals = Math.min(Math.max(maxDecimals, 0), 20);
  const options: Intl.NumberFormatOptions = { maximumFractionDigits: clampedDecimals };
  // Derived traits that round to a fixed number of decimals (round(x, N)) carry that N as
  // max_decimals; pad trailing zeros so the rounding scale is visible (1.2 -> 1.2000).
  if (fixedDecimals && validation.max_decimals != null) {
    options.minimumFractionDigits = clampedDecimals;
  }
  return numericValue.toLocaleString(undefined, options);
}

/**
 * Formats a raw observation value to a string user-friendly representation.
 * @param {OverviewValueType} type - The type of the value.
 * @param {ValidationData} validation - Validation rules for the value.
 * @param {*} value - The raw value to format.
 * @param {boolean} forDataEntry - If true, always shows label for choice types regardless of rating setting.
 * @returns {string}
 */
export function formatValue(
  type: OverviewValueType,
  validation: ValidationData,
  value: {} | null | undefined,
  forDataEntry: boolean = false
): string {
  if (value === undefined || value === null || value === '') {
    return '';
  }
  if (type === 'multi_pictures' && getMultiPicturesUrls(value).length === 0) {
    return '';
  }

  if (type === 'date' || type === 'date_derived') {
    return tryFormatDate(value as string);
  }
  if (type === 'integer') {
    return value.toLocaleString();
  }
  if (type === 'decimal') {
    return formatNumber(value, validation);
  }
  if (type === 'number_derived') {
    return formatNumber(value, validation, true);
  }
  if (type === 'choice') {
    const choice = findById(validation?.options ?? [], value.toString());
    if (forDataEntry) {
      // Always show the name/label in data entry, regardless of rating setting
      return choice?.name ?? '';
    } else {
      // For overview: show numeric value if rating=true, otherwise show label
      if (validation.rating) {
        return choice?.value?.toLocaleString() ?? '';
      } else {
        return choice?.name ?? '';
      }
    }
  }
  if (type === 'string' || type === 'string_long' || type === 'barcode') {
    return value as string;
  }

  return 'OK';
}

/**
 * Extracts the URLs of an observation value of type multi_pictures.
 * @param {*} value - The multi_pictures value.
 * @returns {string[]}
 */
export function getMultiPicturesUrls(value: {}): string[] {
  if (Array.isArray(value)) {
    return new MultiPicturesModel(value).urls();
  } else {
    return [];
  }
}

/**
 * Returns a localized error message when the parsed numeric value is outside
 * the validation min/max bounds, or undefined when within range.
 */
export function getNumericRangeValidationError(
  parsedValue: number,
  validation: ValidationData
): string | undefined {
  if (validation.max_number != null && parsedValue > validation.max_number) {
    return i18n.t('Observation cannot be greater than {{max}}', { max: validation.max_number })();
  }
  if (validation.min_number != null && parsedValue < validation.min_number) {
    return i18n.t('Observation cannot be lower than {{min}}', { min: validation.min_number })();
  }
  return undefined;
}
